import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { CheckCircle2, ChevronDown, ChevronRight, Scale, XCircle } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { AdminGuard } from '@/components/AdminGuard';
import { PageError, PageLoading, Spinner } from '@/components/Spinner';
import { api, type JudgeCalibration, type ReviewComment } from '@/lib/api';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/reviews')({
  component: function ReviewsGuard() {
    return (
      <AdminGuard>
        <div className="space-y-10">
          <OverruleQueue />
          <ReviewsPage />
        </div>
      </AdminGuard>
    );
  },
});

// ────────────────────────────────────────────────────────────────────
// Judge overrule queue — pending "judge is wrong" requests await admin
// approval here. Approving teaches the judge for every future run in
// the same cell + language; rejecting just archives the request.
// ────────────────────────────────────────────────────────────────────

function OverruleQueue() {
  const qc = useQueryClient();
  const calQ = useQuery({
    queryKey: ['calibrations'],
    queryFn: () => api.listCalibrations(),
  });

  const decideMut = useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: 'approved' | 'rejected' }) =>
      api.decideCalibration(id, decision),
    onSuccess: (cal) => {
      toast.success(
        cal.status === 'approved'
          ? `Approved — the judge will honor this from the next ${cal.cell_slug}/${cal.language} run`
          : 'Rejected — the judge is unchanged',
      );
      qc.invalidateQueries({ queryKey: ['calibrations'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (calQ.isLoading) return null;
  const all = calQ.data?.calibrations ?? [];
  const pending = all.filter((c) => c.status === 'pending');
  const decided = all.filter((c) => c.status !== 'pending');

  return (
    <section>
      <div className="flex items-center gap-2">
        <Scale className="size-4 text-status-warning-text" />
        <h1>Judge overrules</h1>
        {pending.length > 0 && (
          <span className="rounded-full bg-status-warning px-2 py-0.5 text-xs font-medium text-status-warning-text">
            {pending.length} pending
          </span>
        )}
      </div>
      <p className="mt-1 text-sm text-muted-foreground">
        Users flagged these judge verdicts as wrong. Approving teaches the judge —
        the correction applies to every persona in the cell from the next run.
        Verdicts already graded never change.
      </p>

      {pending.length === 0 ? (
        <div className="mt-3 rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground shadow-sm">
          No pending overrule requests. Users file them from a failed finding's{' '}
          <strong>Judge wrong?</strong> button on the Runs page.
        </div>
      ) : (
        <div className="mt-3 space-y-3">
          {pending.map((c) => (
            <OverruleCard
              key={c.calibration_id}
              cal={c}
              onDecide={(decision) => decideMut.mutate({ id: c.calibration_id, decision })}
              deciding={decideMut.isPending}
            />
          ))}
        </div>
      )}

      {decided.length > 0 && (
        <details className="mt-4">
          <summary className="cursor-pointer text-xs font-medium text-muted-foreground hover:text-foreground">
            Decided ({decided.length})
          </summary>
          <div className="mt-2 space-y-2">
            {decided.map((c) => (
              <div
                key={c.calibration_id}
                className="rounded-md border border-border bg-card p-3 text-xs"
              >
                <div className="flex items-center gap-2">
                  <span
                    className={cn(
                      'rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide',
                      c.status === 'approved'
                        ? 'bg-success/10 text-success'
                        : 'bg-destructive/10 text-destructive',
                    )}
                  >
                    {c.status}
                  </span>
                  <span className="font-mono text-muted-foreground">
                    {c.cell_slug}/{c.language}
                  </span>
                  <span className="text-muted-foreground">
                    by {c.decided_by} · {c.decided_at ? new Date(c.decided_at).toLocaleString() : ''}
                  </span>
                </div>
                <div className="mt-1.5 text-foreground">{c.watch_for_text}</div>
                <div className="mt-1 text-muted-foreground">{c.reason}</div>
              </div>
            ))}
          </div>
        </details>
      )}
    </section>
  );
}

function OverruleCard({
  cal,
  onDecide,
  deciding,
}: {
  cal: JudgeCalibration;
  onDecide: (decision: 'approved' | 'rejected') => void;
  deciding: boolean;
}) {
  const [showTranscript, setShowTranscript] = useState(false);

  return (
    <article className="rounded-lg border border-status-warning-border/50 bg-card p-4 text-sm shadow-sm">
      <div className="flex flex-wrap items-baseline gap-2 text-xs text-muted-foreground">
        <Link to="/runs" search={{ rid: cal.rid }} className="font-mono hover:underline">
          {cal.rid}
        </Link>
        <span>·</span>
        <span className="font-mono">{cal.cell_slug}/{cal.language}</span>
        <span>·</span>
        <span>persona {cal.persona_id}</span>
        <span>·</span>
        <span>criterion #{cal.watch_for_index}</span>
        <span className="ml-auto">
          requested by {cal.requested_by} · {new Date(cal.requested_at).toLocaleString()}
        </span>
      </div>

      <div className="mt-2 font-medium text-foreground">{cal.watch_for_text}</div>

      <div className="mt-2 grid gap-2 sm:grid-cols-2">
        <div className="rounded-md border border-destructive/20 bg-destructive/5 p-2.5 text-xs">
          <div className="font-medium text-destructive">Judge said (FAIL)</div>
          {cal.judge_evidence_quote && (
            <div className="mt-1 italic text-muted-foreground">
              "{cal.judge_evidence_quote}"
            </div>
          )}
          <div className="mt-1 text-muted-foreground">{cal.judge_reasoning}</div>
        </div>
        <div className="rounded-md border border-status-warning-border/50 bg-status-warning/50 p-2.5 text-xs">
          <div className="font-medium text-status-warning-text">Reviewer says</div>
          <div className="mt-1 whitespace-pre-wrap text-foreground">{cal.reason}</div>
        </div>
      </div>

      <button
        onClick={() => setShowTranscript((v) => !v)}
        className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
      >
        {showTranscript ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
        Full transcript
      </button>
      {showTranscript && <CalTranscript rid={cal.rid} personaId={cal.persona_id} />}

      <div className="mt-3 flex justify-end gap-2">
        <button
          onClick={() => {
            if (window.confirm('Reject this overrule? The judge stays unchanged.')) onDecide('rejected');
          }}
          disabled={deciding}
          className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium text-muted-foreground hover:border-destructive/40 hover:text-destructive disabled:opacity-50"
        >
          <XCircle className="size-3.5" />
          Reject
        </button>
        <button
          onClick={() => {
            if (window.confirm(
              `Approve this overrule?\n\nFrom the next ${cal.cell_slug}/${cal.language} run, the judge will be told this behavior is NOT a failure — for ALL personas. Verdicts already graded do not change.`,
            )) onDecide('approved');
          }}
          disabled={deciding}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {deciding ? <Spinner className="size-3.5" /> : <CheckCircle2 className="size-3.5" />}
          Approve — teach the judge
        </button>
      </div>
    </article>
  );
}

function CalTranscript({ rid, personaId }: { rid: string; personaId: string }) {
  const runQ = useQuery({
    queryKey: ['run', rid],
    queryFn: () => api.getRun(rid),
    staleTime: 60_000,
  });
  if (runQ.isLoading) return <div className="mt-2 text-xs text-muted-foreground">Loading transcript…</div>;
  const pr = runQ.data?.persona_results.find((p) => p.persona_id === personaId);
  if (!pr?.sim) return <div className="mt-2 text-xs text-muted-foreground">No transcript on disk for {personaId}.</div>;
  return (
    <div className="mt-2 max-h-80 space-y-1.5 overflow-y-auto rounded-md border border-border bg-muted/20 p-3">
      {pr.sim.turns.map((t) => (
        <div key={t.index} className="flex gap-2 text-xs">
          <span
            className={cn(
              'w-20 shrink-0 font-medium uppercase tracking-wide',
              t.role === 'agent' ? 'text-primary' : 'text-muted-foreground',
            )}
          >
            {t.role}
          </span>
          <span className="whitespace-pre-wrap">{t.text}</span>
        </div>
      ))}
    </div>
  );
}

function ReviewsPage() {
  const runsQ = useQuery({ queryKey: ['runs'], queryFn: api.listRuns });
  const runIds = runsQ.data?.runs.map((r) => r.run_id) ?? [];
  const commentQs = useQueries({
    queries: runIds.map((rid) => ({
      queryKey: ['comments', rid],
      queryFn: () => api.listComments(rid),
      staleTime: 30_000,
    })),
  });

  if (runsQ.isLoading) return <PageLoading />;
  if (runsQ.error) return <PageError message={String(runsQ.error)} />;

  const allComments: Array<ReviewComment & { runSummary?: ReturnType<typeof getSummary> }> = [];
  function getSummary(rid: string) {
    return runsQ.data?.runs.find((r) => r.run_id === rid);
  }
  for (const q of commentQs) {
    if (!q.data) continue;
    for (const c of q.data.comments) {
      allComments.push({ ...c, runSummary: getSummary(c.rid) });
    }
  }
  allComments.sort((a, b) => (a.created_at < b.created_at ? 1 : -1));

  return (
    <div className="space-y-6">
      <div>
        <h1>Reviews</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Every operator-authored comment across every run.
        </p>
      </div>

      {allComments.length === 0 ? (
        <div className="rounded-lg border border-border bg-card p-6 text-sm text-muted-foreground shadow-sm">
          No comments yet. Open a run, click <strong>Comment</strong> on a failing finding.
        </div>
      ) : (
        <div className="space-y-3">
          {allComments.map((c) => (
            <article
              key={c.comment_id}
              className="rounded-lg border border-border bg-card p-4 text-sm shadow-sm"
            >
              <div className="flex flex-wrap items-baseline gap-2 text-xs text-muted-foreground">
                <Link to="/runs" search={{ rid: c.rid }} className="font-mono hover:underline">
                  {c.rid}
                </Link>
                <span>·</span>
                {c.runSummary && (
                  <>
                    <span>{c.runSummary.cell_slug}</span>
                    <span>·</span>
                    <span>{c.runSummary.prompt_name} v{c.runSummary.prompt_version}</span>
                    <span>·</span>
                  </>
                )}
                <span>persona {c.persona_id}</span>
                {c.watch_for_index >= 0 && (
                  <>
                    <span>·</span>
                    <span>#{c.watch_for_index}</span>
                  </>
                )}
                <span className="ml-auto">{new Date(c.created_at).toLocaleString()}</span>
              </div>
              {c.watch_for_text && (
                <div className="mt-2 text-sm text-foreground">{c.watch_for_text}</div>
              )}
              <div className="mt-2 whitespace-pre-wrap text-sm">{c.body}</div>
              {c.evidence_quote && (
                <div className="mt-2 border-l-2 border-border pl-3 text-xs italic text-muted-foreground">
                  "{c.evidence_quote}"
                </div>
              )}
              <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                <span>by {c.author}</span>
                {c.criterion_ids.length > 0 && (
                  <>
                    <span>·</span>
                    <span>criteria: {c.criterion_ids.join(', ')}</span>
                  </>
                )}
                <span>·</span>
                <span>{c.scribe_version}</span>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
